import { type CSSProperties, type ReactNode } from 'react';
import { fnv1a, mulberry32 } from '@/lib/hash';
import { GRAIN_BACKGROUND } from '@/lib/noise';
import { cn } from '@/lib/utils';

type Range = [min: number, max: number];

type WaveRanges = {
    width: Range;
    height: Range;
    left: Range;
    bottom: Range;
    rotate: Range;
};

/** The back wave reads as the horizon; the front one crosses it. Their ranges
    overlap enough that either can end up dominant. */
const BACK_WAVE: WaveRanges = {
    width: [120, 190],
    height: [50, 95],
    left: [25, 75],
    bottom: [-8, 8],
    rotate: [-14, 14],
};

const FRONT_WAVE: WaveRanges = {
    width: [85, 150],
    height: [30, 70],
    left: [20, 80],
    bottom: [-10, 6],
    rotate: [-18, 18],
};

interface GeneratedCoverProps {
    /**
     * Stable string seeding the wave geometry — an id, not a name, so renaming
     * the thing the cover belongs to leaves its art untouched.
     */
    seed: string;
    /**
     * CSS hue: either an angle (`'199'`) or a token reference
     * (`'var(--cover-hue-3)'`). Every tint on the plate derives from it via the
     * `--cover-base` / `--cover-wave-a` / `--cover-wave-b` / `--cover-ink`
     * lightness+saturation pairs, which is what makes the art theme-aware for
     * free.
     */
    hue: string;
    className?: string;
    /**
     * Overlaid on the art. Position it yourself (the plate is `relative`) —
     * a monogram wants centring, a wordmark wants an edge.
     */
    children?: ReactNode;
}

/**
 * Procedurally generated cover art: a tinted plate, two asymmetric domes, a
 * stipple dither along the front dome's edge, and film grain over the lot.
 *
 * The hue is passed in rather than derived, because callers disagree about what
 * colour *means*: a report template's hue is its own identity (hashed from its
 * id), while an instrument's is its category. The geometry is always seeded, so
 * two things sharing a hue still get different shapes.
 */
export const GeneratedCover = ({ seed, hue, className, children }: GeneratedCoverProps) => {
    const random = mulberry32(fnv1a(seed));
    const between = (min: number, max: number) => min + random() * (max - min);

    const tint = (pair: string) => `hsl(${hue} var(${pair}))`;

    /**
     * A dome rising off the bottom edge — the repo's wave idiom, borrowed from
     * the Arco laudo cover template, but with both corner radii and a rotation
     * drawn per wave. A fixed `50% 50% / 100% 100%` dome is symmetric, and eight
     * symmetric domes read as one card in eight colours; the asymmetry is what
     * makes each cover its own shape.
     *
     * The draws below are order-dependent (see `mulberry32`): inserting or
     * reordering a `between()` call reshuffles every cover already in the wild.
     */
    const wave = (ranges: WaveRanges): CSSProperties => {
        const [topLeft, topRight] = [between(32, 68), between(32, 68)];
        const [riseLeft, riseRight] = [between(72, 100), between(72, 100)];

        return {
            width: `${between(...ranges.width)}%`,
            height: `${between(...ranges.height)}%`,
            left: `${between(...ranges.left)}%`,
            bottom: `${between(...ranges.bottom)}%`,
            borderRadius: `${topLeft}% ${topRight}% 0 0 / ${riseLeft}% ${riseRight}% 0 0`,
            transform: `translateX(-50%) rotate(${between(...ranges.rotate)}deg)`,
            transformOrigin: 'bottom center',
        };
    };

    const backWave = wave(BACK_WAVE);
    const frontWave = wave(FRONT_WAVE);

    return (
        <div
            className={cn('relative size-full overflow-hidden', className)}
            style={{ backgroundColor: tint('--cover-base') }}
        >
            <div
                className="absolute"
                style={{ ...backWave, backgroundColor: tint('--cover-wave-a') }}
            />

            <div
                className="absolute overflow-hidden"
                style={{ ...frontWave, backgroundColor: tint('--cover-wave-b') }}
            >
                {/* Deel's signature edge treatment: a dither of dots crowding the
                    shape's boundary and thinning out into it. Clipped by the dome
                    above, so the stipple follows the curve. */}
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage: `radial-gradient(${`hsl(${hue} var(--cover-ink) / 0.3)`} 0.5px, transparent 0.6px)`,
                        backgroundSize: '5px 5px',
                        maskImage: 'linear-gradient(to bottom, black, transparent 30%)',
                        WebkitMaskImage: 'linear-gradient(to bottom, black, transparent 30%)',
                    }}
                />
            </div>

            {/* Sits under whatever the caller overlays, so lettering stays crisp
                while the plate and both waves take the grain. */}
            <div
                className="absolute inset-0 mix-blend-overlay"
                style={{
                    backgroundImage: GRAIN_BACKGROUND,
                    backgroundSize: '100px 100px',
                    opacity: 0.85,
                }}
            />

            {children}
        </div>
    );
};
